import Phaser from "phaser";
import { LEVELS } from "./levels";
import { starsFor, isUnlocked } from "./storage";
import { domText, domClickable } from "../../dom";

const COLOR_BG = 0xf5e6d3;
const COLOR_CARD = 0xfaf2e8;
const COLOR_CARD_PRESSED = 0xeadac0;
const COLOR_BORDER = 0xd4a574;
const COLOR_LOCKED = 0xe2d4be;
const COLOR_LOCKED_INK = 0xb09b80;

export class FootballSelectScene extends Phaser.Scene {
  constructor() {
    super("football");
  }

  create() {
    this.cameras.main.setBackgroundColor(COLOR_BG);
    this.layout();
    this.scale.on("resize", this.layout, this);
  }

  private layout = () => {
    this.children.removeAll();
    const { width: w, height: h } = this.scale;

    domClickable(this, 36, 28, "←", "arc-hud-back-ink", () => this.scene.start("menu"), 10);
    domText(this, w / 2, 28, "Football", "arc-select-title", 10);

    const cols = 2;
    const rows = Math.ceil(LEVELS.length / cols);
    const sidePad = 24;
    const gridTop = 80;
    const gridBottom = h - 24;
    const gridW = w - sidePad * 2;
    const gridH = gridBottom - gridTop;
    const gap = 14;
    const cellW = (gridW - gap * (cols - 1)) / cols;
    const cellH = (gridH - gap * (rows - 1)) / rows;
    const cellSize = Math.min(cellW, cellH, 180);

    LEVELS.forEach((level, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = w / 2 - ((cols - 1) * (cellSize + gap)) / 2 + col * (cellSize + gap);
      const cy = gridTop + cellSize / 2 + row * (cellSize + gap);
      this.makeCell(cx, cy, cellSize, level.id);
    });
  };

  private makeCell(x: number, y: number, size: number, levelId: number) {
    const unlocked = isUnlocked(levelId);
    const stars = starsFor(levelId);

    const radius = 18;
    const baseFill = unlocked ? COLOR_CARD : COLOR_LOCKED;
    const pressedFill = unlocked ? COLOR_CARD_PRESSED : COLOR_LOCKED;
    const borderColor = unlocked ? COLOR_BORDER : COLOR_LOCKED_INK;

    const gfx = this.add.graphics().setDepth(5);
    const drawBg = (color: number) => {
      gfx.clear();
      gfx.fillStyle(color, 1);
      gfx.fillRoundedRect(x - size / 2, y - size / 2, size, size, radius);
      gfx.lineStyle(2, borderColor, 1);
      gfx.strokeRoundedRect(x - size / 2, y - size / 2, size, size, radius);
    };
    drawBg(baseFill);

    // Number label (DOM, browser-crisp).
    const numEl = document.createElement("div");
    numEl.className = `arc-text arc-cell-num${unlocked ? "" : " arc-cell-num-locked"}`;
    numEl.textContent = String(levelId);
    numEl.style.fontSize = `${Math.round(size * 0.36)}px`;
    this.add.dom(x, y - size * 0.12, numEl).setOrigin(0.5).setDepth(11);

    if (!unlocked) {
      const lockEl = document.createElement("div");
      lockEl.className = "arc-text arc-cell-lock";
      lockEl.textContent = "🔒";
      lockEl.style.fontSize = `${Math.round(size * 0.18)}px`;
      this.add.dom(x, y + size * 0.22, lockEl).setOrigin(0.5).setDepth(11);
    } else {
      const starsEl = document.createElement("div");
      starsEl.className = "arc-text arc-cell-stars";
      for (let i = 0; i < 3; i++) {
        const s = document.createElement("span");
        s.className = i < stars ? "on" : "off";
        s.textContent = "★";
        starsEl.appendChild(s);
      }
      this.add.dom(x, y + size * 0.26, starsEl).setOrigin(0.5).setDepth(11);
    }

    // Hit zone via Phaser graphics container — no DOM clutter for whole cell.
    const zone = this.add
      .zone(x, y, size, size)
      .setRectangleDropZone(size, size)
      .setInteractive({ useHandCursor: unlocked });
    zone.input!.cursor = unlocked ? "pointer" : "default";

    if (unlocked) {
      let pressed = false;
      zone.on("pointerdown", () => {
        pressed = true;
        drawBg(pressedFill);
      });
      const release = (fire: boolean) => {
        if (!pressed) return;
        pressed = false;
        drawBg(baseFill);
        if (fire) this.scene.start("football-play", { levelId });
      };
      zone.on("pointerup", () => release(true));
      zone.on("pointerout", () => release(false));
      zone.on("pointercancel", () => release(false));
    }
  }
}
